import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import TeamLogo from "./TeamLogo";
import { OddsButton } from "./sb";
import { leagueLabel } from "./SBBoardGame";
import { matchupWash } from "@/constants/teamThemes";
import { hasStartedPregame } from "@/lib/sportsbookPriority";

/* FeaturedEventHero — the board marquee + promo card (design, README §2.4).
   Marquee: team-colour wash + (licensed) art slot under a scrim, two team rows,
   moneyline stack ×2 + Event centre button. Promo: only real promotions. */

function mlFor(event, side) {
  return (event.outcomes || []).find((o) => o.market === "moneyline" && o.side === side);
}

export default function FeaturedEventHero({ event, onPick, isPicked, freeBets = 0, promo = null, home = false }) {
  if (!event) return null;
  const isLive = event.status === "live";
  const liveScore = isLive && event.gs_home_score != null;
  const aML = mlFor(event, "away");
  const hML = mlFor(event, "home");
  const started = hasStartedPregame(event); // kicked off, not live -> lock pre-game price
  const teamRow = (name, score, ml) => (
    <div className="flex items-center gap-3">
      <TeamLogo name={name} league={event.league} sport={event.sport} size={42} />
      <span className="min-w-0 flex-1 truncate font-display text-[20px] font-extrabold text-white">{name}</span>
      {score != null && <span className="font-display text-[38px] font-extrabold leading-none tabular-nums text-white">{score}</span>}
    </div>
  );
  return (
    <div className={`grid grid-cols-1 gap-4 ${home ? "lg:grid-cols-[minmax(0,1fr)_300px]" : "lg:grid-cols-[minmax(0,1fr)_248px]"}`}>
      {/* marquee — the WHOLE card links to the event page (odds buttons stopPropagation) */}
      <Link to={`/event/${event.id}`} data-testid="featured-event" className="relative block overflow-hidden" style={{ height: 256, borderRadius: 22, background: "#0E0E11", boxShadow: "inset 0 1px 0 hsl(var(--edge-light)), inset 0 -1px 0 hsl(var(--edge-dark)), 0 0 0 1px hsl(var(--surface-border)), var(--elev-3)", textDecoration: "none" }}>
        {/* art + scrim layer (right 52%) — licensed art slot, empty is fine */}
        <div className="pointer-events-none absolute inset-y-0 right-0" style={{ width: "52%", background: "radial-gradient(120% 120% at 100% 0%, rgba(0,214,50,.10), transparent 60%)" }} />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(90deg, #0E0E11 0%, rgba(14,14,17,.92) 46%, rgba(14,14,17,.45) 100%)" }} />
        {/* subtle matchup wash — away team colour (left) → neutral centre → home team colour (right) */}
        <div className="pointer-events-none absolute inset-0" style={{ background: matchupWash(event, 0.18) }} />
        <div className="relative flex h-full flex-col justify-between p-6">
          <div className="flex items-center gap-2.5">
            <span className="font-display text-[11px] font-extrabold uppercase tracking-[.24em] text-[#00D632]">Featured · {leagueLabel(event.league) || event.sport}</span>
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[.14em] text-[#EF4444]" style={{ background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)" }}>
                <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />LIVE
              </span>
            ) : null}
            {isLive && event.gs_period && <span className="font-display text-[12px] font-bold tabular-nums text-[#E4E4E7]">{event.gs_period}</span>}
            <span className="ml-auto text-[11px] font-semibold text-[#8A8A93]">Game lines · props · SGP</span>
          </div>
          <div className="max-w-[62%] space-y-2.5">
            {teamRow(event.away_team, liveScore ? event.gs_away_score : null, aML)}
            {teamRow(event.home_team, liveScore ? event.gs_home_score : null, hML)}
          </div>
          <div className="flex items-center gap-2">
            <div className="grid flex-1 grid-cols-2 gap-2" style={{ maxWidth: 320 }}>
              {aML ? <OddsButton variant="stack" line={event.away_team?.split(" ").slice(-1)[0]} price={aML.odds} selected={isPicked(event.id, aML.id)} locked={started || aML.suspended === true || aML.active === false} onClick={(e) => { if (e) { e.preventDefault?.(); e.stopPropagation?.(); } onPick(event, aML); }} testid={`odds-hero-${event.id}-away`} /> : <OddsButton na variant="stack" />}
              {hML ? <OddsButton variant="stack" line={event.home_team?.split(" ").slice(-1)[0]} price={hML.odds} selected={isPicked(event.id, hML.id)} locked={started || hML.suspended === true || hML.active === false} onClick={(e) => { if (e) { e.preventDefault?.(); e.stopPropagation?.(); } onPick(event, hML); }} testid={`odds-hero-${event.id}-home`} /> : <OddsButton na variant="stack" />}
            </div>
            <span className="flex h-[54px] items-center gap-1.5 rounded-xl px-4 text-sm font-bold text-white" style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,.16)" }}>
              More <Icon name="chevron-right" size={15} />
            </span>
          </div>
        </div>
      </Link>
      {/* promo card (desktop) */}
      <div className="surface-premium hidden flex-col justify-between overflow-hidden p-5 lg:flex" style={{ height: 256, borderRadius: 22 }}>
        <div>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "#18181C", boxShadow: "inset 0 0 0 1px #26262B" }}><Icon name="promotions" size={17} className="text-[#00D632]" /></span>
          <p className="mt-2 font-display text-[10px] font-extrabold uppercase tracking-[.2em] text-[#71717a]">Free bets</p>
          <h3 className="mt-1 font-display text-[19px] font-extrabold leading-[1.15] text-white" style={{ letterSpacing: "-.02em" }}>{promo ? promo.title : "Free bet tokens"}</h3>
          <p className="mt-1.5 text-[13px] leading-[1.45] text-[#a1a1aa]">{promo ? (promo.description || "") : `Tokens you earn show up in your slip. ${freeBets} available.`}</p>
        </div>
        <Link to="/promotions" className="inline-flex items-center gap-1 text-[13px] font-bold text-[#00D632]">How it works <Icon name="chevron-right" size={14} /></Link>
      </div>
    </div>
  );
}
